import type { ReactElement } from "react";
import { ApacCheckoutComponent, useApacCheckoutStyles } from "@apac/checkout";
import { AddPaymentForm, SelectPayment } from "@apac/components/checkout";
import type { CreditCard } from "@b2bportal/card-api";
import type { UsageType } from "@b2bportal/card-api";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { TokenizeCardErrors } from "@hopper-b2b/types";
import { Dialog, DialogContent } from "@tenant/ui";
import defaultStyles from "./CreditCardPayment.module.scss";

export interface CreditCardPaymentProps {
  title?: string;
  description?: string | ReactElement;
  creditCards: CreditCard[];
  deletePaymentMethod: (paymentId: string) => void;
  disabled?: boolean;
  handleClickAddPaymentMethod: () => void;
  handleCloseAddPaymentMethod: () => void;
  hasError: boolean;
  loading: boolean;
  openAddPaymentMethodModal: boolean;
  selectedPaymentMethodId?: string;
  setOpenErrorPaymentModal: (error: boolean) => void;
  setSelectedPaymentMethodId: (paymentId: string | undefined) => void;
  setTokenizeErrors: (errors: TokenizeCardErrors[]) => void;
  verifyPaymentMethod: (paymentMethod: string, usageType?: UsageType) => void;
  showEditAction?: boolean;
  onEdit?: () => void;
  product?: Product;
  yearDigit?: "two" | "four";
  /** Optional per-tenant predicate restricting which saved cards can be selected. */
  isCardAllowed?: (card: CreditCard) => boolean;
}

export const CreditCardPayment = ({
  title,
  description,
  creditCards,
  disabled = false,
  handleClickAddPaymentMethod,
  handleCloseAddPaymentMethod,
  hasError,
  loading,
  openAddPaymentMethodModal,
  selectedPaymentMethodId,
  setOpenErrorPaymentModal,
  setSelectedPaymentMethodId,
  setTokenizeErrors,
  verifyPaymentMethod,
  showEditAction,
  onEdit,
  product,
  yearDigit,
  isCardAllowed,
}: CreditCardPaymentProps) => {
  const { t } = useI18nContext();
  const styles = useApacCheckoutStyles(
    ApacCheckoutComponent.CreditCardPayment,
    defaultStyles
  );
  const [, cn] = useModuleBEM(styles, ApacCheckoutComponent.CreditCardPayment);

  return (
    <div className={cn("container", { disabled })}>
      <span className={cn("header")}>{title || t("addCard")}</span>
      {description && <span className={cn("subheader")}>{description}</span>}
      {showEditAction && (
        <button className={cn("edit-button")} onClick={onEdit}>
          {t("edit")}
        </button>
      )}
      <Dialog
        open={openAddPaymentMethodModal}
        onClose={handleCloseAddPaymentMethod}
      >
        <DialogContent
          title={t("paymentMethodTitle")}
          className={cn("dialog-content")}
        >
          <AddPaymentForm
            loading={loading}
            onSubmit={(token, _, usageType) => {
              verifyPaymentMethod(token, usageType);
              setSelectedPaymentMethodId(token);
            }}
            onClose={handleCloseAddPaymentMethod}
            onError={(errors) => {
              setTokenizeErrors(errors);
              setOpenErrorPaymentModal(true);
            }}
            yearDigit={yearDigit}
          />
        </DialogContent>
      </Dialog>
      <SelectPayment
        savedPayments={creditCards ?? []}
        selectedPaymentHopperId={selectedPaymentMethodId}
        disabled={disabled || hasError}
        selectPaymentMethod={setSelectedPaymentMethodId}
        handleClickAddPaymentMethod={handleClickAddPaymentMethod}
        product={product}
        isCardAllowed={isCardAllowed}
      />
    </div>
  );
};
